// src/i18n/languages.ts
//
// Single source of truth for languages the app supports.
//
// Each entry has:
//   code         BCP-47 primary subtag ("en", "es", "zh", ...) — stable
//                identifier. Used in cache keys, future translation
//                blob filenames, locale-code matching against
//                navigator.language.
//   englishName  Display name in English. THIS IS WHAT WE STORE on
//                the wire (server `resolveUserLanguage`,
//                travelProfile.demographics.primaryLanguage,
//                localStorage). Matches the existing 20-language
//                list in UserProfile.tsx so we don't fork the system.
//   nativeName   Endonym for picker UI ("Español", "中文",
//                "العربية"). Recognisable to the actual speaker even
//                if the rest of the app is currently English.
//   welcome      Translation of "Welcome" for the rotating gate
//                screen.
//   rank         Lower = shown more prominently / earlier in
//                rotation. Roughly orders by global speakers with a
//                tilt toward common traveler-source-market
//                languages. NOT a strict sociolinguistic claim —
//                just a UI ordering knob.
//   rtl          Optional right-to-left flag for Arabic / Hebrew /
//                Persian. Affects rendering of the welcome word so
//                punctuation/script sits correctly when crossfaded.
//
// To add a language: append an entry. To re-order: bump ranks.
// Don't change `code` or `englishName` once shipped — both are used
// as keys in persisted state and Firestore cache slugs.

export interface LanguageEntry {
  code: string;
  englishName: string;
  nativeName: string;
  welcome: string;
  rank: number;
  rtl?: boolean;
}

export const LANGUAGES: LanguageEntry[] = [
  { code: 'en', englishName: 'English',    nativeName: 'English',           welcome: 'Welcome',           rank: 1 },
  { code: 'es', englishName: 'Spanish',    nativeName: 'Español',           welcome: 'Bienvenido',        rank: 2 },
  { code: 'zh', englishName: 'Chinese',    nativeName: '中文',               welcome: '欢迎',               rank: 3 },
  { code: 'hi', englishName: 'Hindi',      nativeName: 'हिन्दी',              welcome: 'स्वागत है',          rank: 4 },
  { code: 'ar', englishName: 'Arabic',     nativeName: 'العربية',           welcome: 'مرحباً',              rank: 5, rtl: true },
  { code: 'fr', englishName: 'French',     nativeName: 'Français',          welcome: 'Bienvenue',         rank: 6 },
  { code: 'pt', englishName: 'Portuguese', nativeName: 'Português',         welcome: 'Bem-vindo',         rank: 7 },
  { code: 'ru', englishName: 'Russian',    nativeName: 'Русский',           welcome: 'Добро пожаловать',  rank: 8 },
  { code: 'ja', englishName: 'Japanese',   nativeName: '日本語',             welcome: 'ようこそ',           rank: 9 },
  { code: 'de', englishName: 'German',     nativeName: 'Deutsch',           welcome: 'Willkommen',        rank: 10 },
  { code: 'it', englishName: 'Italian',    nativeName: 'Italiano',          welcome: 'Benvenuto',         rank: 11 },
  { code: 'ko', englishName: 'Korean',     nativeName: '한국어',              welcome: '환영합니다',         rank: 12 },
  { code: 'tr', englishName: 'Turkish',    nativeName: 'Türkçe',            welcome: 'Hoş geldiniz',      rank: 13 },
  { code: 'vi', englishName: 'Vietnamese', nativeName: 'Tiếng Việt',        welcome: 'Chào mừng',         rank: 14 },
  { code: 'nl', englishName: 'Dutch',      nativeName: 'Nederlands',        welcome: 'Welkom',            rank: 15 },
  { code: 'pl', englishName: 'Polish',     nativeName: 'Polski',            welcome: 'Witamy',            rank: 16 },
  { code: 'sv', englishName: 'Swedish',    nativeName: 'Svenska',           welcome: 'Välkommen',         rank: 17 },
  { code: 'th', englishName: 'Thai',       nativeName: 'ไทย',                welcome: 'ยินดีต้อนรับ',         rank: 18 },
  { code: 'el', englishName: 'Greek',      nativeName: 'Ελληνικά',          welcome: 'Καλώς ήρθατε',      rank: 19 },
  { code: 'he', englishName: 'Hebrew',     nativeName: 'עברית',             welcome: 'ברוך הבא',           rank: 20, rtl: true },
  { code: 'id', englishName: 'Indonesian', nativeName: 'Bahasa Indonesia',  welcome: 'Selamat datang',    rank: 21 },
  { code: 'no', englishName: 'Norwegian',  nativeName: 'Norsk',             welcome: 'Velkommen',         rank: 22 },
  { code: 'da', englishName: 'Danish',     nativeName: 'Dansk',             welcome: 'Velkommen',         rank: 23 },
  { code: 'fi', englishName: 'Finnish',    nativeName: 'Suomi',             welcome: 'Tervetuloa',        rank: 24 },
  { code: 'cs', englishName: 'Czech',      nativeName: 'Čeština',           welcome: 'Vítejte',           rank: 25 },
  { code: 'hu', englishName: 'Hungarian',  nativeName: 'Magyar',            welcome: 'Üdvözöljük',        rank: 26 },
  { code: 'ro', englishName: 'Romanian',   nativeName: 'Română',            welcome: 'Bun venit',         rank: 27 },
  { code: 'uk', englishName: 'Ukrainian',  nativeName: 'Українська',        welcome: 'Ласкаво просимо',   rank: 28 },
  { code: 'bn', englishName: 'Bengali',    nativeName: 'বাংলা',              welcome: 'স্বাগতম',             rank: 29 },
  { code: 'fa', englishName: 'Persian',    nativeName: 'فارسی',             welcome: 'خوش آمدید',          rank: 30, rtl: true },
];

/** Languages featured in the rotating welcome animation. Top N by rank.
 *  Kept smaller than the full list so the rotation completes a cycle in
 *  a reasonable time — ~30 seconds at 2.5s per word with the default 12. */
export const ROTATION_LANGUAGES: LanguageEntry[] = LANGUAGES
  .slice()
  .sort((a, b) => a.rank - b.rank)
  .slice(0, 12);

/** Languages shown as large/prominent pills in the picker "cloud". */
export const PROMINENT_LANGUAGES: LanguageEntry[] = LANGUAGES
  .slice()
  .sort((a, b) => a.rank - b.rank)
  .slice(0, 8);

/** All supported languages, sorted alphabetically by English name.
 *  Used for the scrollable list below the cloud. */
export const ALPHABETICAL_LANGUAGES: LanguageEntry[] = LANGUAGES
  .slice()
  .sort((a, b) => a.englishName.localeCompare(b.englishName));

/** Look up a language by its `englishName` (the wire format we use). */
export function findByEnglishName(name: string | null | undefined): LanguageEntry | null {
  if (!name) return null;
  return LANGUAGES.find((l) => l.englishName === name) || null;
}

/** Best-effort match against a navigator.language-style locale code
 *  (e.g. "es-ES" → Spanish, "zh-Hans-CN" → Chinese, "pt-BR" → Portuguese).
 *  Returns null on miss. */
export function findByLocale(locale: string | null | undefined): LanguageEntry | null {
  if (!locale) return null;
  const primary = locale.toLowerCase().split(/[-_]/)[0];
  return LANGUAGES.find((l) => l.code === primary) || null;
}

/** Languages where TTS audio narration is supported.
 *
 *  Constrained by the voice + style-instruction coverage wired up on
 *  the server in `functions2/src/enhancedContext.ts`. Any language
 *  outside this set will skip audio generation entirely — the
 *  message is delivered as text only.
 *
 *  KEEP IN SYNC with `TTS_SUPPORTED_LANGUAGE_CODES` in
 *  `functions2/src/enhancedContext.ts`. The client uses this set to
 *  skip the network round-trip up front; the backend uses the same
 *  set as a defensive guard for stale clients / direct callers.
 */
export const AUDIO_SUPPORTED_LANGUAGE_CODES: ReadonlySet<string> = new Set([
  'en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'zh', 'ar', 'ru',
]);

/** Returns true if the given language code (BCP-47 primary subtag, or
 *  full locale like "es-MX" / "zh-Hans") has TTS audio support. */
export function isAudioSupportedLanguage(
  code: string | null | undefined
): boolean {
  if (!code) return false;
  return AUDIO_SUPPORTED_LANGUAGE_CODES.has(
    code.toLowerCase().split(/[-_]/)[0]
  );
}
